import { useNodeManager } from '@/composables/useNodeManager';
import { useProjectSettings } from '@/composables/useProjectSettings';
import { useTreeDiff } from '@/composables/useTreeDiff';
import { useViewportSettings } from '@/composables/useViewportSettings';
import { useAssetStore } from '@/stores/api/asset';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useLedPanelStore } from '@/stores/api/ledpanel';
import { useProjectorStore } from '@/stores/api/projector';
import { useSceneStore } from '@/stores/api/scene';
import { useSelectionStore } from '@/stores/ui/selection';
import { applyOverlaysToObject, cameraControls, hasSceneLoaded, render, scene } from '@/threejs/core';
import { cleanupMeasurementGizmos } from '@/threejs/handlers/measurement';
import { transformControls } from '@/threejs/handlers/transform';
import * as THREE from 'three';
import { toRaw, watch } from 'vue';
import { removeProjector } from './core/projectorHelperLines';
import { OBJECT_TYPES } from './objects';
import { ProjectorTarget } from './objects/projectorTarget';

export function getObjectWidth(object) {
  if (!object) return 1;
  const box = new THREE.Box3().setFromObject(object);
  return box.max.x - box.min.x;
}

let clipboardObject = null;
// Safe clone function that handles Vue reactive objects and complex properties
function safeClone(obj) {
  const raw = toRaw(obj);

  try {
    // First try structuredClone with the raw object
    return structuredClone(raw);
  } catch (error) {
    // If structuredClone fails, use JSON clone as fallback (silently, as this is expected for some objects)
    try {
      return JSON.parse(JSON.stringify(raw));
    } catch (jsonError) {
      // If JSON clone also fails, do a deep manual clone of essential properties
      console.warn('JSON clone failed, using manual clone:', jsonError.message);
      return deepCloneObject(raw);
    }
  }
}

export function copyObject(object) {
  let dataToCopy;
  if (object.isObject3D) {
    dataToCopy = safeClone(object);
    dataToCopy.userData.code = crypto.randomUUID();
  } else {
    dataToCopy = safeClone(object);
  }
  clipboardObject = dataToCopy;
}

export function pasteObject(offset = { x: 1, y: 1, z: 0 }) {
  if (!clipboardObject) return;
  const selectionStore = useSelectionStore();
  const parentCode = selectionStore.selectedObjectId;

  const newObject = safeClone(clipboardObject);

  // Projector position is in newObject.userData.data.position
  if (newObject.userData.data && newObject.userData.data.position) {
    newObject.userData.data.position.x += offset.x;
    newObject.userData.data.position.y += offset.y;
    newObject.userData.data.position.z += offset.z;
  }

  // Add to scene
  addObject(newObject.userData.type, parentCode, newObject.userData);
}

// Manual deep clone for objects that can't be JSON serialized
function deepCloneObject(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepCloneObject(item));
  }

  // For plain objects, clone recursively but skip non-serializable properties
  const cloned = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      // Skip functions, symbols, and other non-cloneable types
      if (typeof value === 'function' || typeof value === 'symbol') {
        continue;
      }
      // Skip DOM elements and complex objects that might cause issues
      if (
        value &&
        typeof value === 'object' &&
        value.constructor &&
        value.constructor.name &&
        !['Object', 'Array', 'Date'].includes(value.constructor.name)
      ) {
        continue;
      }
      cloned[key] = deepCloneObject(value);
    }
  }
  return cloned;
}

// Remove CSS2DObject DOM elements from an object and its children
function disposeCSS2DObjects(object) {
  object.traverse((child) => {
    if (child.isCSS2DObject && child.element && child.element.parentNode) {
      child.element.parentNode.removeChild(child.element);
    }
  });
}

// Don't access stores at module level
let diffTree = null;
const { diffArray } = useTreeDiff();

export function initializeStores() {
  const sceneStore = useSceneStore();
  const cruxModuleStore = useCruxModuleStore();
  const { diffTree: treeDiff } = useTreeDiff();
  diffTree = treeDiff;

  // Set up the watcher after the stores are initialized
  watch(
    () => cruxModuleStore.history,
    ([newSnapshot], [oldSnapshot]) => {
      if (hasSceneLoaded.value) {
        handleDesignChanges(oldSnapshot.snapshot, newSnapshot.snapshot);
      }
    }
  );
  return sceneStore;
}

export function handleDesignChanges(oldSnapshot, newSnapshot) {
  if (!diffTree) {
    // Make sure the stores are initialized
    // initializeStores()
  }

  const changes = diffArray(oldSnapshot, newSnapshot);
  if (changes.added.length) {
    // Sort added objects so parents are processed before children
    const sortedAdded = changes.added.sort((a, b) => {
      const aHasParent = a.data?.parentCode ? 1 : 0;
      const bHasParent = b.data?.parentCode ? 1 : 0;
      return aHasParent - bHasParent;
    });
    sortedAdded.forEach((added) => {
      addObject(added.type, added.parentId, added, null, added);
    });
  }

  if (changes.removed.length) {
    changes.removed.forEach((removed) => {
      removeObject(removed.code);
    });
  }

  if (changes.modified.length) {
    changes.modified.forEach((modified) => {
      updateObject(modified.new.code, modified.new, modified.old);
    });
  }

  render();
}

// Helper function to remove all clone objects from the scene
function removeAllClones() {
  const clonesToRemove = [];
  scene.traverse((object) => {
    if (object.userData && object.userData.clone === true) {
      clonesToRemove.push(object.userData.code);
    }
  });

  clonesToRemove.forEach((uuid) => {
    removeObject(uuid);
  });
}

// Function to create and add a new object to the scene
export async function addObject(type, parentId = null, properties = {}, userData = {}) {
  const { findNodeById } = useNodeManager();

  const cruxModuleStore = useCruxModuleStore();
  if (type == 'geometry') {
    type = properties.data.shape;
  }

  if (type == 'project_settings') {
    const { handleProjectSettingChange } = useProjectSettings();
    // Apply each setting in the properties data
    if (properties.data) {
      Object.keys(properties.data).forEach((key) => {
        handleProjectSettingChange(key, properties.data[key]);
      });
    }
    return null;
  }

  if (type == 'viewport_settings') {
    const { handleViewportSettingChange } = useViewportSettings();
    // Apply each setting in the properties data
    if (properties.data) {
      Object.keys(properties.data).forEach((key) => {
        handleViewportSettingChange(key, properties.data[key]);
      });
    }
    return null;
  }

  if (type == 'timeline_layer') {
    return null;
  }

  if (type == 'camera_bookmark') {
    return null;
  }

  const objectType = OBJECT_TYPES[type];

  if (!objectType) {
    console.error(`Unknown object type: ${type}`);
    return null;
  }

  // does item already exist in the scene?
  const existingObject = getObjectByCode(properties.code);
  if (existingObject) {
    console.warn(`Object with UUID ${properties.code} already exists.`);
    updateObject(properties.code, properties);
    return existingObject;
  }

  // Create the object
  const object = new objectType(properties);
  // Set up userData with the correct ID and defaults
  const objectId = properties.code || crypto.randomUUID();
  object.userData = {
    ...object.userData,
    code: objectId,
    name: properties.name,
    selectable: type === 'infiniteGrid' ? false : true,
    type,
  };

  // Set the object's name to its code so Three.js animation can find it
  object.name = objectId;

  // Ensure clone flag is properly set if passed in properties
  if (properties.clone === true) {
    object.userData.clone = true;
  }

  // Add clonedCode if present in properties.data
  if (properties.data?.clonedCode) {
    object.userData.clonedCode = properties.data.clonedCode;
  }
  object.receiveShadow = true;
  object.castShadow = true;

  // Apply default and custom properties
  const finalProperties = {
    ...objectType.defaultProps,
    ...properties,
    ...object.userData,
  };
  // Add to scene

  if (properties.data.parentCode) {
    const parentObject = getObjectByUserData(scene, 'code', properties.data.parentCode);
    parentObject?.add(object);
  } else {
    scene.add(object);
  }

  if (type === 'projector') {
    const target = new ProjectorTarget(object);
    if (object.userData.clone) {
      target.userData.clone = true;
    }
    scene.add(target);
    target.position.copy(object.userData.target || new THREE.Vector3(0, 0, -5));
  }

  // Apply overlays to newly added mesh (critical for frustum shader overlays)
  if (object.isMesh || object.traverse) {
    applyOverlaysToObject(object);
  }

  if (type === 'model' && finalProperties.data.resource_id) {
    const assetStore = useAssetStore();
    const asset = await assetStore.fetch(finalProperties.data.resource_id);
    finalProperties.data.url = asset.latest_version.file;
  }

  if (finalProperties.data.libraryCode) {
    if (type === 'projector') {
      const projectorStore = useProjectorStore();
      await projectorStore.fetch(finalProperties.data.libraryCode);
    } else if (type === 'led_screen') {
      const ledPanelStore = useLedPanelStore();
      await ledPanelStore.fetch(finalProperties.data.libraryCode);
    }
  }

  if (
    (type === 'projector' || type === 'led_screen') &&
    finalProperties.data?.source &&
    finalProperties.data?.inputSource === 'source'
  ) {
    const assetStore = useAssetStore();
    const asset = await assetStore.fetch(finalProperties.data.source);
    if (asset?.latest_version) {
      finalProperties.data.sourceUrl = asset.latest_version.playback_url || asset.latest_version.file;
      finalProperties.data.type = asset.asset_type;
    }
  }

  // Apply properties
  //applyProperties(object, finalProperties.data, false)

  // If it's a Component node, load children
  if (type === 'component' && finalProperties.componentId) {
    loadComponentChildren(object, finalProperties.componentId, userData);
  }
  if (type !== 'model') {
    const event = new CustomEvent('objectAdded', { detail: { object } });
    window.dispatchEvent(event);
  }

  if ('applyProperties' in object) {
    object.applyProperties(finalProperties.data);
  }

  if (type === 'group') {
    object.refreshClones({
      getChildren: (code) => cruxModuleStore.getSceneTreeModules(code),
      safeClone,
      addObject,
      removeAllClones,
    });
  }

  return object;
}

function updateSymmetryVector3(vector3, properties) {
  vector3 = new THREE.Vector3(vector3.x, vector3.y, vector3.z);
  const plane = properties.symmetryPlane || 'yz';
  const offset = properties.symmetryOffset || 1;

  const matrix = new THREE.Matrix4();
  const scale = new THREE.Vector3(1, 1, 1);
  const translation = new THREE.Vector3(0, 0, 0);

  switch (plane) {
    case 'xy': // Mirror across X-Y plane (flip Z)
      scale.z = -1;
      translation.z = offset * 2;
      break;
    case 'xz': // Mirror across X-Z plane (flip Y)
      scale.y = -1;
      translation.y = offset * 2;
      break;
    case 'yz': // Mirror across Y-Z plane (flip X)
      scale.x = -1;
      translation.x = offset * 2;
      break;
  }

  matrix.makeScale(scale.x, scale.y, scale.z);
  const translationMatrix = new THREE.Matrix4().makeTranslation(translation.x, translation.y, translation.z);
  matrix.premultiply(translationMatrix);

  const vector3out = vector3.applyMatrix4(matrix);
  return { x: vector3out.x, y: vector3out.y, z: vector3out.z };
}

function updateSymmetryRotation(rotation, properties) {
  const plane = properties.symmetryPlane || 'yz';

  // Convert rotation to radians if needed (assuming input is in degrees)
  const rotationRad = {
    x: (rotation.x * Math.PI) / 180,
    y: (rotation.y * Math.PI) / 180,
    z: (rotation.z * Math.PI) / 180,
  };

  let mirroredRotation = { ...rotationRad };

  switch (plane) {
    case 'xy': // Mirror across X-Y plane (flip Z)
      // When mirroring across XY plane, flip rotations around X and Y axes
      mirroredRotation.x = -rotationRad.x;
      mirroredRotation.y = -rotationRad.y;
      break;
    case 'xz': // Mirror across X-Z plane (flip Y)
      // When mirroring across XZ plane, flip rotations around X and Z axes
      mirroredRotation.x = -rotationRad.x;
      mirroredRotation.z = -rotationRad.z;
      break;
    case 'yz': // Mirror across Y-Z plane (flip X)
      // When mirroring across YZ plane, flip rotations around Y and Z axes
      mirroredRotation.y = -rotationRad.y;
      mirroredRotation.z = -rotationRad.z;
      break;
  }

  // Convert back to degrees
  return {
    x: (mirroredRotation.x * 180) / Math.PI,
    y: (mirroredRotation.y * 180) / Math.PI,
    z: (mirroredRotation.z * 180) / Math.PI,
  };
}

function sanitizeVector3(v, defaultValue = { x: 0, y: 0, z: 0 }) {
  // Handle case where v is undefined or null
  if (!v) {
    return new THREE.Vector3(defaultValue.x, defaultValue.y, defaultValue.z);
  }
  return new THREE.Vector3(v.x ?? defaultValue.x, v.y ?? defaultValue.y, v.z ?? defaultValue.z);
}

function updateLinearArrayVector3(v, properties, arrayIndex) {
  const vector3 = new THREE.Vector3(v.x, v.y, v.z);
  const arrayTranslation = sanitizeVector3(properties.arrayTranslation, {
    x: 2,
    y: 0,
    z: 0,
  });
  const arrayRotation = sanitizeVector3(properties.arrayRotation);
  // Apply translation for this array index
  const translationVector = new THREE.Vector3(
    arrayTranslation.x * arrayIndex || 0,
    arrayTranslation.y * arrayIndex || 0,
    arrayTranslation.z * arrayIndex || 0
  );

  // Apply rotation for this array index (convert degrees to radians)
  const rotationEuler = new THREE.Euler(
    (arrayRotation.x * arrayIndex * Math.PI) / 180 || 0,
    (arrayRotation.y * arrayIndex * Math.PI) / 180 || 0,
    (arrayRotation.z * arrayIndex * Math.PI) / 180 || 0
  );

  // Create transformation matrix
  const matrix = new THREE.Matrix4();
  matrix.makeRotationFromEuler(rotationEuler);

  // Apply rotation around origin, then translate
  const vector3out = vector3.applyMatrix4(matrix).add(translationVector);

  return { x: vector3out.x, y: vector3out.y, z: vector3out.z };
}

// Function to get all objects in the scene
export function getAllObjects() {
  const objects = [];
  scene.traverse((object) => {
    if (object.userData && object.userData.uuid) {
      objects.push(object);
    }
  });
  return objects;
}

// Function to get object by ID

export function getObjectByCode(id) {
  let found = null;
  scene.traverse((object) => {
    if (object.userData.code === id) {
      found = object;
    }
  });
  return found;
}

// Function to get object by ID
export function getObjectByName(name) {
  let found = null;
  scene.traverse((object) => {
    if (object.name === name) {
      found = object;
    }
  });
  return found;
}

// Function to remove object by ID
export function removeObject(id) {
  const object = getObjectByCode(id);

  if (!object) {
    console.error(`Object with ID ${id} not found`);
    return;
  }

  // Detach transform controls synchronously before removing from scene
  const selectionStore = useSelectionStore();
  if (selectionStore.selectedObjectId === id || selectionStore.selectedObjectId === id + '-target') {
    if (transformControls) transformControls.detach();
    selectionStore.clearSelection();
  }

  // Clean up measurement endpoint gizmos if this is a measurement
  if (object.userData.type === 'measurement') {
    cleanupMeasurementGizmos(id);
  }

  // Clean up projector helper lines and associated target (including children of groups)
  object.traverse((child) => {
    if (child.userData?.isProjector) {
      removeProjector(child);
      const target = getObjectByCode(child.userData.code + '-target');
      if (target) {
        disposeCSS2DObjects(target);
        if (target.parent) target.parent.remove(target);
      }
    }
  });

  // Clean up projector helper lines and associated target
  if (object.userData.isProjector) {
    object.dispose?.();
    const target = getObjectByCode(id + '-target');
    if (target) {
      // Explicitly remove CSS2DObject children to fire their 'removed' event
      // (handles cases where element.parentNode is null / not yet rendered)
      const css2dChildren = [];
      target.traverse((child) => {
        if (child.isCSS2DObject) css2dChildren.push(child);
      });
      css2dChildren.forEach((child) => {
        if (child.parent) child.parent.remove(child);
      });
      if (target.parent) target.parent.remove(target);
    }
  }

  // Remove CSS2D DOM elements from this object and its children
  disposeCSS2DObjects(object);

  // Remove from parent
  if (object.parent) {
    object.parent.remove(object);
  }

  // Dispose of geometries, textures and materials
  if (object.geometry) object.geometry.dispose();

  if (object.material) {
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach((material) => {
      // Dispose all texture properties
      Object.keys(material).forEach((key) => {
        if (material[key] && material[key].isTexture) {
          material[key].dispose();
        }
      });
      // Dispose the material itself
      material.dispose();
    });
  }

  const event = new CustomEvent('objectRemoved', { detail: { object } });
  window.dispatchEvent(event);
  render();
}

// Function to update object properties
export function updateObject(id, properties, oldProperties = null) {
  const cruxModuleStore = useCruxModuleStore();
  // Handle settings objects first
  if (properties.type === 'project_settings') {
    const { handleProjectSettingChange } = useProjectSettings();
    // Apply each setting in the properties data
    if (properties.data) {
      Object.keys(properties.data).forEach((key) => {
        if (oldProperties && oldProperties.data && oldProperties.data[key] === properties.data[key]) {
          return;
        }
        handleProjectSettingChange(key, properties.data[key]);
      });
    }
    return;
  }

  if (properties.type === 'viewport_settings') {
    const { handleViewportSettingChange } = useViewportSettings();
    // Apply each setting in the properties data
    if (properties.data) {
      Object.keys(properties.data).forEach((key) => {
        if (oldProperties && oldProperties.data && oldProperties.data[key] === properties.data[key]) {
          return;
        }

        handleViewportSettingChange(key, properties.data[key]);
      });
    }
    return;
  }

  const object = getObjectByCode(id);

  const { findNodeById } = useNodeManager();
  const parent = findNodeById(properties.data.parentCode);

  // Handle parent change - reparent the object in Three.js scene
  if (object && oldProperties?.data?.parentCode !== properties.data.parentCode) {
    const oldParentCode = oldProperties?.data?.parentCode;
    const newParentCode = properties.data.parentCode;

    // Remove from old parent
    if (oldParentCode) {
      const oldParentObject = getObjectByCode(oldParentCode);
      if (oldParentObject) {
        oldParentObject.remove(object);
      }
    } else {
      // Was a top-level object, remove from scene
      scene.remove(object);
    }

    // Add to new parent
    if (newParentCode) {
      const newParentObject = getObjectByCode(newParentCode);
      if (newParentObject) {
        newParentObject.add(object);
      }
    } else {
      // Moving to top level
      scene.add(object);
    }

    // Apply overlays to moved object (only if it has new materials)
    applyOverlaysToObject(object);

    // If the old parent was a group, update it
    if (oldParentCode) {
      const oldParentNode = findNodeById(oldParentCode);
      if (oldParentNode?.type === 'group') {
        updateObject(oldParentCode, safeClone(oldParentNode));
      }
    }
  }
  if (object) {
    object.applyProperties(properties.data);
    if (properties.name) {
      object.userData.name = properties.name;
    }
    if (properties.type === 'group') {
      object.refreshClones({
        getChildren: (code) => cruxModuleStore.getSceneTreeModules(code),
        safeClone,
        addObject,
        removeAllClones,
      });
    } else if (
      (properties.type === 'projector' || properties.type === 'led_screen') &&
      properties.data.code !== oldProperties?.data?.code
    ) {
      removeObject(properties.code);
      addObject(properties.type, properties.data.parentCode, properties);
      render();
      return;
    }

    const event = new CustomEvent('objectUpdated', { detail: { object } });
    window.dispatchEvent(event);
  } else {
    addObject(properties.type, null, properties);
  }

  if (parent?.type == 'group') {
    updateObject(parent.code, safeClone(parent));
  }
}

export function zoomToObject(id) {
  const object = getObjectByCode(id);

  const box = new THREE.Box3().setFromObject(object);
  const sphere = box.getBoundingSphere(new THREE.Sphere());

  cameraControls.fitToSphere(sphere, true);
}

export function getObjectsByUserData(object, key, value, result) {
  if (typeof result === 'undefined') result = [];
  // check the current object
  if (object.userData[key] === value) result.push(object);

  // check children
  for (var i = 0, l = object.children.length; i < l; i++) {
    var child = object.children[i];
    getObjectsByUserData(child, key, value, result);
  }
  return result;
}

export function getObjectByUserData(object, uuid, value) {
  if (object.userData[uuid] === value) return object;

  for (let i = 0, l = object.children.length; i < l; i++) {
    const child = object.children[i];
    const foundObject = getObjectByUserData(child, uuid, value);

    if (foundObject !== undefined) {
      return foundObject;
    }
  }
  return undefined;
}

export function shouldSkipObject(object) {
  if (object.renderOrder < 0) return true;
  if (object.type === 'Line2') return true;
  if (object.type === 'LineSegments2') return true;
  // if (object.userData.type === 'Plane') return true
  if (object.isSprite) return true;
  if (object.userData.isHelper) return true;
  if (object.userData.excludeFromOutline) return true;
  if (object.userData?.isMeasurementGroundPlane === true) return true;

  // Skip invisible objects
  if (!object.visible) return true;

  // Skip projector meshes (the actual projector body)
  if (object.userData.type === 'projector') return true;
  if (object.userData.isProjectorTarget) return true;
  if (object.userData.isProjectorMesh) return true;

  // Skip measurement objects
  if (object.userData.isMeasurement) return true;

  if (object.userData.projectorOverlayIgnore) return true;

  if (object.constructor.name === 'CSS2DObject') return true;

  return false;
}

export function shouldSkipAll(object) {
  // Skip helper
  if (object.isLight || object.isCamera) return true;
  // Skip TransformControls components (plane, gizmo, and root)
  if (object.isTransformControlsPlane) return true;
  if (object.isTransformControlsGizmo) return true;
  if (object.isTransformControlsRoot) return true;

  if (object.userData.isGrid) return true;
  if (object.userData.isGizmo) return true;

  if (object.userData?.type === 'infiniteGrid') return true;
  if (object.parent?.userData.type === 'infiniteGrid') return true;
  if (object.constructor.name === 'InfiniteGrid') return true;

  if (shouldSkipObject(object)) return true;

  return false;
}
